import React, { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "../../Componenets/Toast/Toast";
import prenderFoco from "../../Shared/Foco/prenderFoco";
import {
  IconAlerta,
  IconCaptura,
  IconFechar,
  IconLixeira,
  IconNota,
  IconRecarregar,
  IconStatus,
} from "../../Shared/Icons/Icons";
import StatusBadge from "./StatusBadge";
import {
  alterarStatusProposta,
  buscarProposta,
  excluirProposta,
} from "./propostasApi";
import {
  OBSERVACAO_MAXIMA,
  STATUS,
  formatarCep,
  formatarData,
  formatarDataHora,
  formatarDocumento,
  formatarMoeda,
  rotuloCanal,
  rotuloStatus,
  tempoRelativo,
} from "./propostaFormato";

const TITULO_ID = "proposta-drawer-titulo";

function Resumo({ proposta }) {
  const { cliente } = proposta;
  const itens = [
    ["Cliente", cliente?.razaoSocial || "Não identificado"],
    ["CNPJ/CPF", cliente ? formatarDocumento(cliente.documento) : "—"],
    ["CEP", formatarCep(cliente?.cep)],
    ["Vendedor", proposta.vendedor || "—"],
    ["Emissão", formatarData(proposta.dataEmissao)],
    ["Valor total", formatarMoeda(proposta.valorTotal)],
    ["Prazo de entrega", proposta.prazoEntrega || "—"],
    ["Enviada por", rotuloCanal(proposta.canalEnvio)],
    [
      "Capturas",
      proposta.capturas === 1 ? "1 vez" : `${proposta.capturas} vezes`,
    ],
  ];

  return (
    <section className="drawer-section" aria-labelledby="drawer-resumo">
      <h3 id="drawer-resumo" className="section-title">
        Resumo
      </h3>
      <dl className="proposta-resumo">
        {itens.map(([rotulo, valor]) => (
          <div key={rotulo} className="proposta-resumo-item">
            <dt>{rotulo}</dt>
            <dd>{valor}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function FormStatus({ proposta, onAlterada }) {
  const toast = useToast();
  const [status, setStatus] = useState(proposta.status);
  const [observacao, setObservacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const mudouStatus = status !== proposta.status;
  const temObservacao = observacao.trim() !== "";
  const podeSalvar = (mudouStatus || temObservacao) && !salvando;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!podeSalvar) return;

    setSalvando(true);
    setErro("");
    try {
      const resultado = await alterarStatusProposta(proposta.numero, {
        status,
        observacao: observacao.trim(),
      });
      setObservacao("");
      onAlterada(resultado);
      toast.sucesso(
        mudouStatus
          ? `Proposta Nº ${proposta.numero} marcada como ${rotuloStatus(status)}.`
          : `Observação registrada na proposta Nº ${proposta.numero}.`,
      );
    } catch (error) {
      setErro(error.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <section className="drawer-section" aria-labelledby="drawer-status">
      <h3 id="drawer-status" className="section-title">
        Atualizar status
      </h3>
      <form className="status-form" onSubmit={handleSubmit}>
        <fieldset className="status-opcoes">
          <legend className="sr-only">Novo status</legend>
          {STATUS.map(({ valor, rotulo }) => (
            <label key={valor} className="status-opcao">
              <input
                type="radio"
                name="status-proposta"
                value={valor}
                checked={status === valor}
                onChange={() => setStatus(valor)}
              />
              <span className={`status-opcao-chip status-opcao-chip--${valor}`}>
                <span className="status-badge-dot" aria-hidden="true" />
                {rotulo}
                {valor === proposta.status && (
                  <span className="status-opcao-atual">(atual)</span>
                )}
              </span>
            </label>
          ))}
        </fieldset>

        <div className="field">
          <label htmlFor="proposta-observacao" className="field-label">
            Observação <span className="field-opcional">(opcional)</span>
          </label>
          <textarea
            id="proposta-observacao"
            className="field-input status-observacao"
            rows={3}
            maxLength={OBSERVACAO_MAXIMA}
            placeholder="Ex.: cliente pediu desconto de 5% no frete"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            aria-describedby="proposta-observacao-ajuda"
          />
          <p id="proposta-observacao-ajuda" className="field-hint">
            Fica registrada no histórico. {observacao.length}/
            {OBSERVACAO_MAXIMA}
          </p>
        </div>

        {erro && (
          <div className="alert alert-error" role="alert">
            <IconAlerta size={18} />
            <span>{erro}</span>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={!podeSalvar}
          aria-busy={salvando}
        >
          {salvando && <span className="spinner" aria-hidden="true" />}
          {salvando
            ? "Salvando…"
            : mudouStatus || !temObservacao
              ? "Salvar status"
              : "Registrar observação"}
        </button>
      </form>
    </section>
  );
}

function descreverEvento(evento, primeiraCaptura) {
  if (evento.tipo === "captura") {
    return {
      Icone: IconCaptura,
      variante: "captura",
      titulo: primeiraCaptura
        ? "Proposta capturada do Tiny"
        : "Capturada novamente do Tiny",
    };
  }

  if (evento.statusAnterior === evento.statusNovo) {
    return {
      Icone: IconNota,
      variante: "nota",
      titulo: "Observação registrada",
    };
  }

  return { Icone: IconStatus, variante: "status", titulo: "Status alterado" };
}

function Historico({ eventos }) {
  const idPrimeiraCaptura = [...eventos]
    .reverse()
    .find((evento) => evento.tipo === "captura")?.id;

  return (
    <section className="drawer-section" aria-labelledby="drawer-historico">
      <h3 id="drawer-historico" className="section-title">
        Histórico
      </h3>
      {eventos.length === 0 ? (
        <p className="field-hint">Nenhum evento registrado.</p>
      ) : (
        <ol className="proposta-log">
          {eventos.map((evento) => {
            const { Icone, variante, titulo } = descreverEvento(
              evento,
              evento.id === idPrimeiraCaptura,
            );
            const mostraTransicao =
              evento.tipo === "status" &&
              evento.statusAnterior !== evento.statusNovo;

            return (
              <li key={evento.id} className={`log-item log-item--${variante}`}>
                <span className="log-icone" aria-hidden="true">
                  <Icone size={16} />
                </span>
                <div className="log-conteudo">
                  <div className="log-cabecalho">
                    <p className="log-titulo">{titulo}</p>
                    <time
                      className="log-hora"
                      dateTime={evento.criadoEm}
                      title={tempoRelativo(evento.criadoEm)}
                    >
                      {formatarDataHora(evento.criadoEm)}
                    </time>
                  </div>
                  {mostraTransicao && (
                    <p className="log-transicao">
                      {evento.statusAnterior && (
                        <>
                          <StatusBadge status={evento.statusAnterior} />
                          <span aria-hidden="true">→</span>
                          <span className="sr-only">para</span>
                        </>
                      )}
                      <StatusBadge status={evento.statusNovo} />
                    </p>
                  )}
                  {evento.observacao && (
                    <blockquote className="log-observacao">
                      {evento.observacao}
                    </blockquote>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function ExcluirProposta({ numero, onExcluida }) {
  const toast = useToast();
  const [confirmando, setConfirmando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [erro, setErro] = useState("");
  const abrirRef = useRef(null);
  const cancelarRef = useRef(null);
  const montadoRef = useRef(false);

  // Na confirmacao o foco vai para "Cancelar" (a opcao segura); ao desistir, volta.
  useEffect(() => {
    if (!montadoRef.current) {
      montadoRef.current = true;
      return;
    }
    (confirmando ? cancelarRef : abrirRef).current?.focus();
  }, [confirmando]);

  const excluir = async () => {
    setExcluindo(true);
    setErro("");
    try {
      await excluirProposta(numero);
    } catch (error) {
      // 404: ja tinha sido excluida (outra aba, outra pessoa). O resultado e o mesmo.
      if (error.status !== 404) {
        setErro(error.message);
        setExcluindo(false);
        return;
      }
    }
    toast.sucesso(`Proposta Nº ${numero} excluída.`);
    onExcluida(numero);
  };

  return (
    <section className="drawer-section" aria-labelledby="drawer-excluir">
      <h3 id="drawer-excluir" className="section-title">
        Excluir proposta
      </h3>
      {confirmando ? (
        <div className="excluir-confirmacao">
          <p id="excluir-pergunta" className="excluir-pergunta">
            Excluir a proposta Nº {numero}? O histórico também é apagado e não
            dá para desfazer.
          </p>
          <div className="excluir-acoes">
            <button
              type="button"
              className="btn btn-perigo btn-sm"
              onClick={excluir}
              disabled={excluindo}
              aria-busy={excluindo}
              aria-describedby="excluir-pergunta"
            >
              {excluindo && <span className="spinner" aria-hidden="true" />}
              {excluindo ? "Excluindo…" : "Sim, excluir"}
            </button>
            <button
              ref={cancelarRef}
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setConfirmando(false);
                setErro("");
              }}
              disabled={excluindo}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="field-hint">
            Remove a proposta da lista junto com o histórico. O cliente continua
            salvo.
          </p>
          <button
            ref={abrirRef}
            type="button"
            className="btn btn-secondary btn-sm excluir-botao"
            onClick={() => setConfirmando(true)}
          >
            <IconLixeira size={16} />
            Excluir proposta
          </button>
        </>
      )}
      {erro && (
        <div className="alert alert-error" role="alert">
          <IconAlerta size={18} />
          <span>{erro}</span>
        </div>
      )}
    </section>
  );
}

export default function ProposalDrawer({
  numero,
  onFechar,
  onAlterada,
  onExcluida,
}) {
  const drawerRef = useRef(null);
  const fecharRef = useRef(null);
  const [estado, setEstado] = useState({
    carregando: true,
    erro: null,
    dados: null,
  });
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    const controle = new AbortController();
    setEstado({ carregando: true, erro: null, dados: null });

    buscarProposta(numero, { signal: controle.signal })
      .then((dados) => setEstado({ carregando: false, erro: null, dados }))
      .catch((error) => {
        if (error.name === "AbortError") return;
        setEstado({ carregando: false, erro: error, dados: null });
      });

    return () => controle.abort();
  }, [numero, versao]);

  // Foco no botao de fechar ao abrir; Esc fecha; a pagina por tras nao rola.
  useEffect(() => {
    fecharRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onFechar();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("drawer-aberto");
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("drawer-aberto");
    };
  }, [onFechar]);

  const handleAlterada = useCallback(
    ({ proposta, evento }) => {
      setEstado((atual) => ({
        ...atual,
        dados: { proposta, eventos: [evento, ...atual.dados.eventos] },
      }));
      onAlterada(proposta);
    },
    [onAlterada],
  );

  const { carregando, erro, dados } = estado;

  return (
    <>
      <div className="drawer-overlay" aria-hidden="true" onClick={onFechar} />
      <div
        ref={drawerRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITULO_ID}
        onKeyDown={(event) => prenderFoco(event, drawerRef.current)}
      >
        <header className="drawer-header">
          <div className="drawer-header-texto">
            <h2 id={TITULO_ID} className="drawer-title">
              Proposta Nº {numero}
            </h2>
            {dados && <StatusBadge status={dados.proposta.status} />}
          </div>
          <button
            ref={fecharRef}
            type="button"
            className="btn btn-ghost drawer-close"
            aria-label="Fechar detalhes da proposta"
            onClick={onFechar}
          >
            <IconFechar />
          </button>
        </header>

        <div className="drawer-body" aria-busy={carregando}>
          {carregando && (
            <div className="drawer-carregando" role="status">
              <span className="spinner" aria-hidden="true" />
              Carregando proposta…
            </div>
          )}

          {erro && (
            <div className="alert alert-error drawer-erro" role="alert">
              <IconAlerta size={18} />
              <div className="alert-conteudo">
                <span>{erro.message}</span>
                {erro.status !== 404 && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setVersao((v) => v + 1)}
                  >
                    <IconRecarregar size={16} />
                    Tentar novamente
                  </button>
                )}
              </div>
            </div>
          )}

          {dados && (
            <>
              <Resumo proposta={dados.proposta} />
              {/* So monta com os dados carregados, entao o useState ja nasce com o status atual. */}
              <FormStatus
                proposta={dados.proposta}
                onAlterada={handleAlterada}
              />
              <Historico eventos={dados.eventos} />
              <ExcluirProposta numero={numero} onExcluida={onExcluida} />
            </>
          )}
        </div>
      </div>
    </>
  );
}
